import { useEffect } from "react";
import { useLoadMore } from "@/hooks/useLoadMore";
import { fetchCarList, type CarProductItem } from "@/api/car";
import { useCarFilterStore } from "@/store/carFilterStore";
import CarCard from "./CarCard";

interface CarListProps {
  activeBrand: number;
  activeCategory: number;
  sort: string;
  coords: { latitude: string; longitude: string };
  isReplaceMode: boolean;
  onSelect: (id: number) => void;
}

export default function CarList({
  activeBrand,
  activeCategory,
  sort,
  coords,
  isReplaceMode,
  onSelect,
}: CarListProps) {
  const brands = useCarFilterStore((state) => state.brands);
  const categories = useCarFilterStore((state) => state.categories);
  const loadFilters = useCarFilterStore((state) => state.loadFilters);

  // 上拉加载
  const {
    list: carList,
    total,
    loading,
    hasMore,
    reload,
    containerRef,
  } = useLoadMore<CarProductItem>({
    deps: [
      activeBrand,
      activeCategory,
      sort,
      coords.latitude,
      coords.longitude,
    ],
    fetcher: async (page) => {
      const brandName =
        activeBrand === 0 ? undefined : brands[activeBrand - 1]?.brand;
      const categoryName =
        activeCategory === 0
          ? undefined
          : categories[activeCategory - 1]?.car_type;

      return fetchCarList({
        page,
        limit: 10,
        brand: brandName,
        car_type: categoryName,
        sort: sort,
        longitude: coords.longitude,
        latitude: coords.latitude,
      });
    },
  });

  // 加载筛选条件
  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const currentBrandName =
    activeBrand === 0 ? "全部" : brands[activeBrand - 1]?.brand || "全部";

  return (
    <div
      ref={containerRef}
      className="flex-1 min-w-0 px-3 py-2 pb-10 space-y-3 overflow-y-auto"
      style={{ maxHeight: "calc(100vh - 200px)" }}
    >
      <div className="pb-1">
        <span className="text-xs text-gray-400">
          {currentBrandName} · 共{" "}
          <span className="font-semibold text-gray-700">{total}</span> 款车型
        </span>
      </div>

      {loading && carList.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <span className="text-xs text-gray-400">加载中...</span>
        </div>
      ) : (
        <>
          {carList.map((car) => (
            <CarCard
              key={car.id}
              car={car}
              isReplaceMode={isReplaceMode}
              onSelect={onSelect}
            />
          ))}

          {loading && carList.length > 0 && (
            <div className="text-center py-4">
              <span className="text-xs text-gray-400">加载中...</span>
            </div>
          )}

          {!hasMore && carList.length > 0 && (
            <div className="text-center py-4">
              <span className="text-xs text-gray-400">没有更多了</span>
            </div>
          )}

          {carList.length === 0 && !loading && (
            <div className="text-center py-10">
              <span className="text-xs text-gray-400">暂无车辆</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
